import { trackEvent as utilsTrackEvent, consoleEvents, CustomTrackers } from '@pb/utils'

import {
  BOPIS_TYPE,
  CHECKOUT_CATEGORY_TRACKING_DATA,
  CHECKOUT_TRACKING_EVENTS,
  CROSSBRANDS,
  DROPSHIP_TYPE,
  HM_BRAND,
  RESELL_TYPE,
} from 'features/checkout/constants'
import checkoutSelectors from 'features/checkout/duck/selectors'
import { Item, Parcel } from 'features/checkout/types'
import { TrackErrorParams } from 'features/checkout/types/tracking'
import { buildCustomTrackers } from 'helpers/tracking'
import { ThunkResult } from 'redux/types'

const checkoutTrackingUtils = (customer: unknown): CustomTrackers =>
  buildCustomTrackers({
    staticDataMap: CHECKOUT_TRACKING_EVENTS,
    persistentData: customer,
  })

export const trackEvent =
  ({
    event,
    clickTale,
    add,
  }: {
    event: string
    clickTale?: string
    add?: any // can be anything that should be added to the tracking object
  }): ThunkResult<void> =>
  (_, getState) => {
    if (process.env.NODE_ENV !== 'production') {
      /* eslint-disable-next-line no-console */
      consoleEvents.info('Tracking ContentSquare event: ', event)
    }
    checkoutTrackingUtils(checkoutSelectors.getCustomerTrackingData(getState())).trackEvent(
      event,
      clickTale,
    )(add)
  }

export const trackViewEvent =
  (event: string, clickTale?: string): ThunkResult<void> =>
  (_, getState) => {
    const state = getState()
    return checkoutTrackingUtils(checkoutSelectors.getCustomerTrackingData(state)).trackViewEvent(
      event,
      clickTale,
    )(checkoutSelectors.getProductsTrackingData(state))
  }

export const trackError =
  ({ section, message, id, errorType }: TrackErrorParams): ThunkResult<void> =>
  (_, getState): void =>
    checkoutTrackingUtils(checkoutSelectors.getCustomerTrackingData(getState())).trackEvent(
      CHECKOUT_TRACKING_EVENTS.error.id,
    )({
      error_function: section,
      ...(message && { error_message: message }),
      ...(id && { error_id: id }),
      ...(errorType && { error_type: errorType }),
    })

export const trackingUtilsEvents = (
  isCrossBrandsBasket: boolean,
  isDropShip: boolean,
  hasMultipleParcels: boolean,
  hasResellItem: boolean,
): void => {
  if (isCrossBrandsBasket) {
    utilsTrackEvent({
      ...CHECKOUT_CATEGORY_TRACKING_DATA,
      event_type: 'CHECKOUT_CBS',
      event_id: 'CBS items in cart',
      event_category: 'Checkout entry',
    })
  }
  if (isDropShip) {
    utilsTrackEvent({
      ...CHECKOUT_CATEGORY_TRACKING_DATA,
      event_type: 'CHECKOUT_dropship',
      event_id: 'dropship item in cart',
      event_category: 'Checkout entry',
    })
  }

  if (hasMultipleParcels) {
    utilsTrackEvent({
      ...CHECKOUT_CATEGORY_TRACKING_DATA,
      event_type: 'CHECKOUT_multiple_parcels',
      event_id: 'multiple parcel item in cart',
      event_category: 'Checkout entry',
    })
  }

  if (hasResellItem) {
    utilsTrackEvent({
      ...CHECKOUT_CATEGORY_TRACKING_DATA,
      event_type: 'CHECKOUT_resell',
      event_id: 'resell item in cart',
      event_category: 'Checkout entry',
    })
  }
}

export const trackingUtilsEventsForResell = (itemCondition: string): void => {
  utilsTrackEvent({
    ...CHECKOUT_CATEGORY_TRACKING_DATA,
    event_type: 'CHECKOUT_resell',
    event_id: 'resell item in cart',
    event_category: 'Checkout entry',
    event_label: itemCondition,
  })
}

export const getTrackingProductsInParcel = (items: Item[], parcels: Parcel[]): string[] => {
  const itemsInOrderByParcel = items.map((item) => {
    const parcelIndex = parcels.findIndex((parcel) => {
      return parcel.entries.some((parcelItem) => parcelItem.variantCode === item.variantCode)
    })
    return `${parcelIndex + 1}`
  })

  return itemsInOrderByParcel
}

export const getTrackingProductParcelTypes = (items: Item[], parcels: Parcel[]): string[] => {
  const itemsOrderedByParcelType = items.map((item) => {
    const itemsParcel = parcels.find((parcel) => {
      return parcel.entries.some((parcelItem) => parcelItem.variantCode === item.variantCode)
    })

    if (itemsParcel?.consignmentType === BOPIS_TYPE) {
      return 'bopis'
    }

    if (CROSSBRANDS.includes(itemsParcel?.consignmentType || '')) {
      return 'cbs'
    }
    if (itemsParcel?.consignmentType === DROPSHIP_TYPE) {
      return 'dropship'
    }
    if (HM_BRAND.includes(itemsParcel?.consignmentType || '')) {
      const isOrderOrchestration =
        parcels.filter((parcel) => HM_BRAND.includes(parcel.consignmentType)).length > 1

      if (isOrderOrchestration) {
        return 'order orchestration'
      }
      return 'internal'
    }
    if (itemsParcel?.consignmentType === RESELL_TYPE) {
      return 'resell'
    }
    return itemsParcel?.consignmentType || ''
  })
  return itemsOrderedByParcelType
}
